/**
 * Green Klean® Vacuum Bags & Filters — full content from gogreenklean.com/vacuum-bags-filters/
 * Mounted at /marketplace/green-klean/vacuum-bags-filters
 */

import { Link } from "react-router-dom";
import {
  ArrowLeft, ShoppingCart, Phone, Download, FileText, ShieldCheck,
  Filter, Boxes, Award, Leaf, ExternalLink, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuoteCart } from "@/contexts/QuoteCartContext";
import MarketplaceCategoryNav from "@/components/MarketplaceCategoryNav";
import MarketplaceBreadcrumb from "@/components/MarketplaceBreadcrumb";

const ASSET_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/static/green-kleen/vacuum-bags`;
const PDF = (n) => `${ASSET_BASE}/pdfs/${n}`;
const THUMB = (n) => `${ASSET_BASE}/thumbs/${n}`;
const GK_LOGO = `${process.env.REACT_APP_BACKEND_URL}/api/static/green-kleen/images/gk-logo.png`;

const SALES_SHEETS = [
  { thumb: THUMB("Bag-Filter-Sales-Flyer-8-25-1.jpg"),       file: PDF("Bag-Filter-Sales-Flyer-8-25.pdf"),                   title: "Clean with Confidence — Sales Flyer" },
  { thumb: THUMB("2025-Vac-Bag-sales-flier-1.jpg"),          file: PDF("2025-Vac-Bag-sales-flier.pdf"),                      title: "Replacement Bags & Filters Sales Flyer" },
  { thumb: THUMB("GK-ProTeam-1.jpg"),                        file: PDF("GK-ProTeam.pdf"),                                    title: "ProTeam Bags & Filters Flyer" },
  { thumb: THUMB("Windsor-Karcher-Replacement-Bags-Filters-1.jpg"), file: PDF("Windsor-Karcher-Replacement-Bags-Filters.pdf"), title: "Windsor Karcher Bags & Filters Flyer" },
];

const FEATURES = [
  {
    icon: ShieldCheck,
    title: "Built for Commercial Performance",
    body: "Designed to endure rigorous daily use, our bags are crafted with premium-quality, 2- or 3-ply construction and Micro-Plus filtration media. Count on them to resist tearing, control dust, and reduce interruptions from leaks or changes.",
  },
  {
    icon: Filter,
    title: "Superior Filtration",
    body: "Our Micro-Plus filter media — running silky smooth yet tough — captures up to 99.9% of dust, pollen, and allergens, keeping floors cleaner and spaces healthier.",
  },
  {
    icon: Boxes,
    title: "OEM Compatibility",
    body: "From Advance to Hoover, Nilfisk to Bissell, Green Klean® covers over 125 SKUs. Each product is clearly labeled for easy identification and guaranteed to match or exceed OEM standards.",
  },
  {
    icon: Award,
    title: "Green Diamond Warranty®",
    body: "Enjoy peace of mind that lives up to our promise. Every bag and filter is backed by our exclusive warranty — because reliable performance matters.",
    pdf: PDF("GK-Green-Diamond-Warranty.pdf"),
    pdfLabel: "Read Warranty",
  },
  {
    icon: Leaf,
    title: "Made Responsibly",
    body: "You expect value and safety. We deliver both — without compromise. Experience professional-grade reliability in one affordable, eco-thoughtful package.",
  },
];

const OEM_BRANDS = [
  "Advance", "Bissell", "Carpet Pro", "Clarke", "Dyson", "Eureka", "Hoover",
  "Karcher", "Kent", "Kirby", "Minuteman", "NaceCare", "Nilfisk",
  "NSS", "Oreck", "Panasonic", "Pacific", "Panasonic", "Powr-Flite",
  "ProTeam", "Riccar", "Royal", "Sandia", "Sanitaire", "Simplicity",
  "Tornado", "Windsor",
];

export default function GreenKleenVacuumBagsPage() {
  const { addItem, setShowCart, cart } = useQuoteCart();
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  const addBundle = () => {
    addItem({
      erp: "GK-VAC-BAGS-INQUIRY",
      name: "Green Klean® Vacuum Bags & Filters — Quote Inquiry",
      image: THUMB("Bag-Filter-Sales-Flyer-8-25-1.jpg"),
      source: "green-klean-vacuum-bags",
      category: "Vacuum Bags & Filters",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/40 via-white to-emerald-50/40" data-testid="gk-vacuum-bags-page">
      <MarketplaceCategoryNav currentCategory="green-klean" />
      <MarketplaceBreadcrumb trail={[{ label: "Green Klean®", to: "/marketplace/green-klean" }, { label: "Vacuum Bags & Filters" }]} />
      {/* Top bar */}
      <header className="bg-gradient-to-r from-emerald-900 via-emerald-800 to-teal-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/marketplace/green-klean"
            className="flex items-center gap-2 text-emerald-200 hover:text-white"
            data-testid="vac-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back to Green Klean®</span>
          </Link>
          <div className="flex items-center gap-3">
            <a href="tel:2814563458" className="hidden sm:flex items-center gap-1.5 text-sm text-emerald-200 hover:text-white">
              <Phone className="w-4 h-4" /> 281.456.3458
            </a>
            <Button
              onClick={() => setShowCart(true)}
              size="sm"
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold relative"
              data-testid="vac-open-cart-btn"
            >
              <ShoppingCart className="w-4 h-4 mr-1.5" /> Quote Cart
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center" data-testid="vac-cart-count">
                  {cartCount}
                </span>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-3 text-xs text-slate-500">
          <Link to="/marketplace" className="hover:text-emerald-700">Marketplace</Link>
          <span className="mx-1.5">/</span>
          <Link to="/marketplace/green-klean" className="hover:text-emerald-700">Green Klean®</Link>
          <span className="mx-1.5">/</span>
          <span className="text-slate-700 font-medium">Vacuum Bags &amp; Filters</span>
        </div>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600 text-white">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `radial-gradient(circle at 25% 50%, white 1px, transparent 1px), radial-gradient(circle at 75% 30%, white 1px, transparent 1px)`,
          backgroundSize: "70px 70px",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 py-16">
          <div className="flex items-center gap-3 mb-5">
            <div className="bg-white rounded-lg px-3 py-2 inline-flex items-center shadow-md">
              <img src={GK_LOGO} alt="Green Klean®" className="h-7 w-auto" />
            </div>
            <Badge className="bg-emerald-400/30 text-emerald-50 border border-emerald-300/40 backdrop-blur">
              Authorized Distributor · Sterile Mate
            </Badge>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.05] mb-4 max-w-3xl" style={{ fontFamily: "'Outfit','Segoe UI',sans-serif" }}>
            Clean Smarter, Breathe Easier with<br />
            <span className="text-emerald-200">Green Klean® Vacuum Bags &amp; Filters</span>
          </h1>
          <p className="text-emerald-50/90 text-lg leading-relaxed max-w-3xl mb-7">
            At Green Klean®, we redefine what clean means — and our Premium Vacuum Bags &amp; Filters
            deliver on that promise. Engineered for professionals and trusted in high-demand
            environments, these essentials offer unmatched durability, high-efficiency filtration,
            and serious value. <strong>Sterile Mate is an authorized Green Klean® distributor</strong> —
            quote and ship through our team.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              size="lg"
              onClick={() => { addBundle(); setShowCart(true); }}
              className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold shadow-xl"
              data-testid="vac-hero-quote-btn"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              Request Bag &amp; Filter Quote
            </Button>
            <a
              href="https://gogreenklean.com/product-category/filters-bags/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="vac-full-lineup-btn"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold text-sm backdrop-blur"
            >
              See our entire lineup of vacuum bags &amp; filters
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* WHY SECTION */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <p className="text-xs uppercase tracking-wider font-bold text-emerald-700 mb-2">Why Green Klean® Vacuum Bags &amp; Filters?</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">5 reasons facilities switch to Green Klean®</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {FEATURES.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 hover:border-emerald-400 hover:shadow-xl transition-all p-6 flex flex-col" data-testid={`vac-feature-${i}`}>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-4 shadow-md">
                <f.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2 leading-tight">{f.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed flex-1">{f.body}</p>
              {f.pdf && (
                <a
                  href={f.pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  data-testid={`vac-feature-pdf-${i}`}
                  className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-800"
                >
                  <Download className="w-3.5 h-3.5" /> {f.pdfLabel}
                </a>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* OEM COMPATIBILITY STRIP */}
      <section className="bg-slate-900 text-white py-14">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Badge className="bg-emerald-500/20 text-emerald-200 border border-emerald-300/30 mb-3">125+ SKUs</Badge>
          <h2 className="text-2xl sm:text-3xl font-bold mb-2">Cross-references &amp; OEM-equivalent for the brands you already use</h2>
          <p className="text-slate-400 text-sm mb-8">Each Green Klean® bag &amp; filter is clearly labeled for easy identification and guaranteed to match or exceed OEM standards.</p>
          <div className="flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
            {OEM_BRANDS.map((b, i) => (
              <span key={i} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-slate-300">{b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* SALES SHEETS */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="vac-sales-sheets-section">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-slate-900 mb-2">Sales Sheets</h2>
          <p className="text-slate-600 italic">Clean with confidence. Clean the Green Klean® way.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SALES_SHEETS.map((s, i) => (
            <a
              key={i}
              href={s.file}
              target="_blank"
              rel="noopener noreferrer"
              download
              data-testid={`vac-sheet-${i}`}
              className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-emerald-400 hover:shadow-lg transition-all flex flex-col"
            >
              <div className="aspect-[4/5] bg-slate-50 overflow-hidden">
                <img src={s.thumb} alt={s.title} className="w-full h-full object-contain p-2 group-hover:scale-[1.02] transition-transform" />
              </div>
              <div className="p-4 flex items-start justify-between gap-3 border-t border-slate-100">
                <p className="text-sm font-semibold text-slate-800 leading-tight flex-1 line-clamp-3">{s.title}</p>
                <Download className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0 group-hover:translate-y-0.5 transition-transform" />
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* FULL LINEUP CTA */}
      <section className="max-w-5xl mx-auto px-6 pb-16">
        <div className="bg-gradient-to-br from-emerald-700 to-teal-700 rounded-2xl text-white p-8 text-center shadow-xl">
          <h3 className="text-2xl sm:text-3xl font-bold mb-3">See our entire product lineup of vacuum bags and filters</h3>
          <p className="text-emerald-100 mb-6 max-w-2xl mx-auto">Browse the complete 125+ SKU catalog with full OEM cross-references on the Green Klean® site.</p>
          <a
            href="https://gogreenklean.com/product-category/filters-bags/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="vac-cta-full-lineup-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold shadow-md"
          >
            ALL VACUUM BAGS and FILTERS <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* QUOTE CART CTA */}
      <section className="max-w-5xl mx-auto px-6 pb-16 text-center">
        <h3 className="text-2xl font-bold text-slate-900 mb-3">Ready to source through Sterile Mate?</h3>
        <p className="text-slate-600 mb-5">As an authorized Green Klean® distributor, we'll quote, ship, and service your replenishment program — single SKU or 125 SKUs.</p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Button size="lg" onClick={() => { addBundle(); setShowCart(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-md" data-testid="vac-bottom-quote-btn">
            <ShoppingCart className="w-5 h-5 mr-2" /> Add Inquiry to Quote Cart
          </Button>
          <Button size="lg" variant="outline" asChild className="border-emerald-600 text-emerald-700 hover:bg-emerald-50">
            <a href="tel:2814563458" data-testid="vac-call-btn">
              <Phone className="w-5 h-5 mr-2" /> Call (281) 456-3458
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
}
